import React, { useEffect, useState } from 'react';
import { Copy, KeyRound, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

// Inbound Mirror pulse feed: per-org API keys + sample payload for POST /api/transfer-health/pulses.
export default function MirrorFeedDialog({ open, onOpenChange, organizationId }) {
  const [data, setData] = useState(null);
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const orgQ = organizationId && organizationId !== '_all' ? `?organization_id=${encodeURIComponent(organizationId)}` : '';

  const load = () => api.get(`/transfer-health/pulse-keys${orgQ}`).then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.detail || e.message));
  useEffect(() => { if (open) { setNewKey(null); load(); } }, [open, organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      const body = { label: 'Mirror' };
      if (organizationId && organizationId !== '_all') body.organization_id = organizationId;
      const r = await api.post('/transfer-health/pulse-keys', body);
      setNewKey(r.data.key); load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this key? Mirror will stop being able to push pulses with it.')) return;
    await api.delete(`/transfer-health/pulse-keys/${id}${orgQ}`); load();
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Copied'); };
  const endpoint = `${process.env.REACT_APP_BACKEND_URL}${data?.endpoint || '/api/transfer-health/pulses'}`;
  const sample = JSON.stringify(data?.sample || {}, null, 2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="mirror-feed-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> Mirror pulse feed</DialogTitle>
          <DialogDescription className="text-xs">Mirror pushes confidence pulses to Seerios with an org API key; each pulse lands on the learner&apos;s Confidence node for that month (multiple pulses in a month are averaged). Learners are matched to cohorts by email via the roster.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="space-y-3 text-xs">
            {newKey && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-1" data-testid="mirror-new-key">
                <p className="font-semibold text-emerald-900">New key — shown once, store it in Mirror now</p>
                <div className="flex items-center gap-2"><code className="flex-1 break-all rounded bg-white px-2 py-1 text-[11px]" data-testid="mirror-new-key-value">{newKey}</code><Button size="sm" variant="outline" onClick={() => copy(newKey)} data-testid="mirror-copy-key"><Copy className="h-3.5 w-3.5" /></Button></div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="font-semibold text-slate-800">Active keys ({data.keys.length})</p>
              <Button size="sm" onClick={create} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="mirror-create-key"><Plus className="h-3.5 w-3.5 mr-1" /> Generate key</Button>
            </div>
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200" data-testid="mirror-key-list">
              {data.keys.length === 0 && <li className="px-3 py-2 text-slate-500">No keys yet.</li>}
              {data.keys.map((k) => (
                <li key={k.id} className="flex items-center gap-3 px-3 py-2" data-testid={`mirror-key-${k.id}`}>
                  <code className="text-[11px]">{k.prefix}…</code>
                  <span className="text-slate-500 flex-1">{k.label} · created {new Date(k.created_at).toLocaleDateString()} · {k.pulses_received || 0} pulses{k.last_used_at ? ` · last ${new Date(k.last_used_at).toLocaleDateString()}` : ''}</span>
                  <button type="button" onClick={() => revoke(k.id)} className="text-slate-400 hover:text-rose-600" data-testid={`mirror-revoke-${k.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Integration</p>
              <pre className="rounded-md bg-slate-900 text-slate-100 p-3 text-[10.5px] overflow-x-auto" data-testid="mirror-sample">{`POST ${endpoint}\nX-Seerios-Key: thk_…\nContent-Type: application/json\n\n${sample}`}</pre>
              <p className="text-slate-500 mt-1">Fields: learner_email (required), recorded_at or period (YYYY-MM[-DD]), confidence 0–100, optional capability and cohort. Unmatched learners are reported back as skipped rows.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
